import { useCallback, useMemo } from "react";

import { Order } from "~generated/graphqlTypes";
import { useLocalStorage } from "./useLocalStorage";

export type SortState<K extends string> = { key?: K; order?: Order };
export type SortStateKey<O> = Extract<keyof O, string>;

function toInitialSortState<K extends string>(key: K | undefined, order: Order | null | undefined): SortState<K> {
  if (!key || (order !== Order.Asc && order !== Order.Desc)) {
    return {};
  }

  return { key, order };
}

/**
 * A generic hook to manage sort state returning the expected `order` object to pass directly to GQL queries
 * i.e. toggleSort("foo") will return { foo: Order.Asc } / { foo: Order.Desc }
 * */
export function useSortState<O extends Record<string, Order | null | undefined>>(
  storageKey: string,
  initialKey: SortStateKey<O>,
  initialOrder: Order | null | undefined,
) {
  const [sortState, setSortState] = useLocalStorage<SortState<SortStateKey<O>>>(
    storageKey,
    toInitialSortState(initialKey, initialOrder),
  );

  const toggleSort = useCallback(
    (key: SortStateKey<O>) => {
      if (sortState.key === key) {
        setSortState({ key, order: sortState.order === Order.Asc ? Order.Desc : Order.Asc });
        return;
      }

      // When not toggling the correct sort key, set the new key to ascending
      setSortState({ key, order: Order.Asc });
    },
    [setSortState, sortState],
  );

  const order = useMemo(() => {
    if (!sortState.key || !sortState.order) {
      return undefined;
    }

    return { [sortState.key]: sortState.order } as O;
  }, [sortState]);

  return { sortState, setSortState, toggleSort, order };
}
